import { finalize } from "rxjs/operators";
import { ChangeDetectionStrategy, Component, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-common-route',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './route.component.html',
    styleUrls: ['./route.component.scss'],
    standalone: false
})
export class RouteComponent {
  #isSubmitted = signal(false);
  node = signal<FlatModels.NodeEntityModels.NodeEntityType | null>(null);
  page = signal<FlatModels.PageEntityModels.PageEntityType | null>(null);
  
  routeForm = new FormGroup(
    {
      route: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.NodeEntityModels.NodeSchema.pick({
          route: true,
        }).refine((val) => {
          const siblings = this.nodeSharedFacade.snapshot_currentSelectedSiblingNodes();
          return !(siblings || []).some(s => s.route === val?.route);
        }, {
          message: "notification.route_already_exists",
          path: ["route"],
        }).refine((val) => {
          return !FlatModels.NodeEntityModels.ReservedRouteNames.has(val.route || '');
        }, {
          message: "notification.route_use_reserved_keyword",
          path: ["route"],
        })
      ),
      updateOn: 'change',
    }
  );
  
  constructor(private nodeSharedFacade: FlatFacades.NodeSharedFacade,
              private alertControlFacade: FlatFacades.AlertControlFacade) {

    this.nodeSharedFacade.currentSelectedPage$.pipe(takeUntilDestroyed()).subscribe((page) => {
      this.page.set(structuredClone(page));
    });
    this.nodeSharedFacade.currentSelectedNode$.pipe(takeUntilDestroyed()).subscribe((node) => {
      this.node.set(structuredClone(node));
      if (node) {
        this.routeForm.setValue({
          route: node.route,
        });
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onRouteSubmit() {
    const node = this.node();
    if (this.#isSubmitted() || this.routeForm.invalid || !node || !this.routeForm.value.route) {
      return;
    }
    this.#isSubmitted.set(true);
    
    this.nodeSharedFacade.requestUpdateNodeRoute(node.id, this.routeForm.value.route)
      .pipe(
        finalize(() => this.#isSubmitted.set(false))
      ).subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.routeForm);
          },
    });
  }
  
  onRouteReset() {
    const node = this.node();
    const page = this.page();
    if (!node || !page) return;
    const newRoute = this.nodeSharedFacade.requestGetNodeRouteFromTitle(node.id, page.title)
    if (newRoute && newRoute !== this.routeForm.value.route) {
      this.routeForm.setValue({
        route: newRoute
      })
      this.onRouteSubmit();
    }
  }
}
